import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useSnackbar } from 'notistack';
import { v4 as uuidv4 } from 'uuid';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Unstable_Grid2';
import Button from '@mui/material/Button';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Stack from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import { Link as RouterLink } from 'react-router-dom';
import { Icon } from '@iconify/react';

import cloudApi from '../../../app/api/cloudApi';
import TextInputComponent from '../../../components/input/TextInputComponent';
import WeeklyScheduleGrid from './components/WeeklyScheduleGrid';
import SchedulePresets from './components/SchedulePresets';
import AccessLevelPreview from './components/AccessLevelPreview';

// Day names for display
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Stable row id for Suprema gates (deviceId + gateId from box API). */
const supremaGateKey = (g) => `${g.deviceId}-${g.gateId}`;

// Create empty day schedule
const createEmptyDay = () => ({
  enabled: false,
  periods: []
});

// Create default weekly schedule (all days empty)
const createEmptyWeeklySchedule = () => ({
  days: DAY_NAMES.map(() => createEmptyDay())
});

// Create "Always" schedule (24/7)
const createAlwaysSchedule = () => ({
  days: DAY_NAMES.map(() => ({
    enabled: true,
    periods: [{ startMinutes: 0, endMinutes: 1440 }] // 0:00 to 24:00
  }))
});

// Create "Never" schedule (no access)
const createNeverSchedule = () => ({
  days: DAY_NAMES.map(() => ({
    enabled: false,
    periods: []
  }))
});

// Create "Weekdays" schedule (Mon-Fri, 6am-6pm)
const createWeekdaysSchedule = () => ({
  days: DAY_NAMES.map((_, index) => ({
    enabled: index >= 1 && index <= 5, // Monday (1) to Friday (5)
    periods: index >= 1 && index <= 5 ? [{ startMinutes: 360, endMinutes: 1080 }] : [] // 6:00 to 18:00
  }))
});

// Create "Business Hours" schedule (Mon-Fri, 9am-5pm)
const createBusinessHoursSchedule = () => ({
  days: DAY_NAMES.map((_, index) => ({
    enabled: index >= 1 && index <= 5,
    periods: index >= 1 && index <= 5 ? [{ startMinutes: 540, endMinutes: 1020 }] : [] // 9:00 to 17:00
  }))
});

export default function AccessLevelDesigner() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  
  const [loading, setLoading] = useState(true);
  const [fetchingFromDevices, setFetchingFromDevices] = useState(false);
  const [pushingToDevice, setPushingToDevice] = useState(false);
  const [site, setSite] = useState(null);
  const [error, setError] = useState(null);
  
  // Access level state
  const [accessLevelName, setAccessLevelName] = useState('');
  const [scheduleType, setScheduleType] = useState('custom'); // 'always', 'never', 'weekdays', 'business', 'custom'
  const [weeklySchedule, setWeeklySchedule] = useState(createEmptyWeeklySchedule());
  const [savedAccessLevels, setSavedAccessLevels] = useState([]);
  const [targetType, setTargetType] = useState('net2');
  const [availableAreas, setAvailableAreas] = useState([]);
  const [selectedAreaIds, setSelectedAreaIds] = useState([]);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [availableGates, setAvailableGates] = useState([]);
  const [selectedGateKeys, setSelectedGateKeys] = useState([]);
  const [loadingGates, setLoadingGates] = useState(false);

  // Load site data
  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user || !siteId) {
      setLoading(false);
      return;
    }

    const loadSiteData = async () => {
      try {
        const response = await cloudApi.getSite(auth.user.access_token, siteId);
        setSite(response.data);
      } catch (err) {
        console.error('Error loading site:', err);
        setError('Failed to load site data');
        enqueueSnackbar('Failed to load site data', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadSiteData();
  }, [auth.isAuthenticated, auth.user, siteId, enqueueSnackbar]);

  // Fetch Net2 areas once site is loaded
  useEffect(() => {
    if (!site || !auth.user) return;
    handleTargetTypeChange('net2');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site]);

  // Handle preset selection
  const handlePresetChange = (preset) => {
    setScheduleType(preset);
    
    switch (preset) {
      case 'always':
        setWeeklySchedule(createAlwaysSchedule());
        break;
      case 'never':
        setWeeklySchedule(createNeverSchedule());
        break;
      case 'weekdays':
        setWeeklySchedule(createWeekdaysSchedule());
        break;
      case 'business':
        setWeeklySchedule(createBusinessHoursSchedule());
        break;
      case 'custom':
      default:
        // Keep current schedule for custom
        break;
    }
  };

  // Handle schedule changes from the grid
  const handleScheduleChange = (newSchedule) => {
    setWeeklySchedule(newSchedule);
    setScheduleType('custom'); // Switch to custom when manually editing
  };

  // Build the unified contract
  const buildContract = () => {
    let type = scheduleType;
    
    // Determine type based on schedule content
    const allDaysEnabled = weeklySchedule.days.every(d => d.enabled);
    const noDaysEnabled = weeklySchedule.days.every(d => !d.enabled);
    const allDays247 = weeklySchedule.days.every(d => 
      d.enabled && 
      d.periods.length === 1 && 
      d.periods[0].startMinutes === 0 && 
      d.periods[0].endMinutes === 1440
    );
    
    if (noDaysEnabled) {
      type = 'never';
    } else if (allDays247) {
      type = 'always';
    } else {
      type = 'weekly';
    }

    return {
      id: uuidv4(),
      name: accessLevelName || 'Unnamed Access Level',
      siteId: siteId,
      type: type,
      weeklySchedule: weeklySchedule,
      holidays: {
        enabled: false,
        schedule: null
      },
      createdAt: new Date().toISOString()
    };
  };

  const handleTargetTypeChange = async (val) => {
    if (!val) return;
    setTargetType(val);
    if (val === 'net2' && availableAreas.length === 0) {
      setLoadingAreas(true);
      try {
        const response = await cloudApi.getAccessLevelAreas(auth.user.access_token, siteId, site?.accountId);
        if (response.data && Array.isArray(response.data)) {
          setAvailableAreas(response.data);
          setSelectedAreaIds(response.data.map(a => a.areaID ?? a.areaId));
        }
      } catch (err) {
        console.error('Error fetching areas:', err);
        enqueueSnackbar('Failed to fetch areas from Net2', { variant: 'warning' });
      } finally {
        setLoadingAreas(false);
      }
    }
    if (val === 'suprema' && availableGates.length === 0) {
      setLoadingGates(true);
      try {
        const response = await cloudApi.getAccessLevelGates(auth.user.access_token, siteId, site?.accountId);
        if (response.data && Array.isArray(response.data)) {
          setAvailableGates(response.data);
          setSelectedGateKeys(response.data.map(supremaGateKey));
        }
      } catch (err) {
        console.error('Error fetching gates:', err);
        enqueueSnackbar('Failed to fetch gates from Suprema', { variant: 'warning' });
      } finally {
        setLoadingGates(false);
      }
    }
  };

  const handleAreaToggle = (areaId) => {
    setSelectedAreaIds(prev =>
      prev.includes(areaId) ? prev.filter(id => id !== areaId) : [...prev, areaId]
    );
  };

  const handleSupremaGateToggle = (key) => {
    setSelectedGateKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // Handle fetch from devices
  const handleFetchFromDevices = async () => {
    setFetchingFromDevices(true);
    try {
      const response = await cloudApi.fetchAccessLevelsFromDevices(auth.user.access_token, siteId, site?.accountId);
      if (response.data && Array.isArray(response.data)) {
        setSavedAccessLevels(response.data);
        enqueueSnackbar(`Fetched ${response.data.length} access levels from devices`, { variant: 'success' });
      }
    } catch (err) {
      console.error('Error fetching access levels:', err);
      enqueueSnackbar('Failed to fetch access levels from devices', { variant: 'error' });
    } finally {
      setFetchingFromDevices(false);
    }
  };

  // Handle push to device
  const handlePushToDevice = async () => {
    if (!accessLevelName.trim()) {
      enqueueSnackbar('Please enter a name for the access level', { variant: 'warning' });
      return;
    }

    setPushingToDevice(true);
    try {
      const contract = buildContract();
      // Box expects Net2 area IDs, or Suprema device IDs (gates on those devices are applied server-side).
      const idsToSend =
        targetType === 'suprema'
          ? [
              ...new Set(
                availableGates
                  .filter((g) => selectedGateKeys.includes(supremaGateKey(g)))
                  .map((g) => g.deviceId)
              ),
            ]
          : selectedAreaIds;
      const response = await cloudApi.pushAccessLevelToDevice(auth.user.access_token, siteId, site?.accountId, contract, targetType, idsToSend);
      
      if (response.data && response.data.success) {
        enqueueSnackbar(`Access level pushed to ${targetType.toUpperCase()} successfully`, { variant: 'success' });
        // Add to local list for prototype feedback
        setSavedAccessLevels(prev => [...prev, contract]);
        
        // Reset form
        setAccessLevelName('');
        setScheduleType('custom');
        setWeeklySchedule(createEmptyWeeklySchedule());
      } else {
        enqueueSnackbar(`Failed to push to ${targetType.toUpperCase()}`, { variant: 'error' });
      }
    } catch (err) {
      console.error('Error pushing access level:', err);
      enqueueSnackbar('Failed to push access level to device', { variant: 'error' });
    } finally {
      setPushingToDevice(false);
    }
  };

  // Handle save (local only for now)
  const handleSave = () => {
    if (!accessLevelName.trim()) {
      enqueueSnackbar('Please enter a name for the access level', { variant: 'warning' });
      return;
    }

    const contract = buildContract();
    setSavedAccessLevels(prev => [...prev, contract]);
    enqueueSnackbar('Access level saved locally', { variant: 'success' });
    
    // Reset form
    setAccessLevelName('');
    setScheduleType('custom');
    setWeeklySchedule(createEmptyWeeklySchedule());
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/sites');
  };

  // Handle load saved access level into designer
  const handleLoadSaved = (al) => {
    setAccessLevelName(al.name || '');
    if (al.weeklySchedule?.days?.length === 7) {
      setWeeklySchedule(al.weeklySchedule);
      setScheduleType('custom');
    } else {
      setWeeklySchedule(createEmptyWeeklySchedule());
      setScheduleType('custom');
    }
    enqueueSnackbar(`Loaded "${al.name}" into designer`, { variant: 'info' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle delete saved access level
  const handleDeleteSaved = (id) => {
    setSavedAccessLevels(prev => prev.filter(al => al.id !== id));
    enqueueSnackbar('Access level removed', { variant: 'info' });
  };

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button 
          variant="contained" 
          sx={{ mt: 2 }}
          onClick={() => navigate('/sites')}
        >
          Return to Sites
        </Button>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{site ? `${site.name} - Access Level Designer | CloudLoq` : 'Access Level Designer | CloudLoq'}</title>
      </Helmet>

      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link component={RouterLink} to="/sites" underline="hover" color="inherit">
              Sites
            </Link>
            <Typography color="text.primary">Access Level Designer</Typography>
          </Breadcrumbs>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Icon icon="mdi:clock-time-four-outline" width="32" height="32" />
            <Typography variant="h4">
              Access Level Designer {site && `- ${site.name}`}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={handleFetchFromDevices}
            disabled={fetchingFromDevices}
            startIcon={fetchingFromDevices ? <CircularProgress size={20} /> : <Icon icon="mdi:download-network" />}
          >
            Fetch from Devices
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Design time-based access schedules for your profiles. Define when users can access the site by configuring allowed time periods for each day of the week.
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          {/* Left Column - Schedule Designer */}
          <Grid xs={12} lg={8}>
            <Card sx={{ mb: 3 }}>
              <CardHeader 
                title="Schedule Configuration"
                subheader="Define the name and time periods for this access level"
              />
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid xs={12} md={6}>
                    <TextInputComponent
                      value={accessLevelName}
                      setValue={setAccessLevelName}
                      label="Access Level Name"
                      placeholder="e.g., Day Shift, Night Crew, Contractors"
                    />
                  </Grid>
                  <Grid xs={12} md={6}>
                    <SchedulePresets 
                      value={scheduleType}
                      onChange={handlePresetChange}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Card>

            <Card>
              <CardHeader 
                title="Weekly Schedule"
                subheader="Click on a day to add time periods, or drag to create time blocks"
                action={
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setWeeklySchedule(createEmptyWeeklySchedule());
                      setScheduleType('custom');
                    }}
                    startIcon={<Icon icon="mdi:refresh" />}
                  >
                    Clear All
                  </Button>
                }
              />
              <Box sx={{ p: 3 }}>
                <WeeklyScheduleGrid 
                  schedule={weeklySchedule}
                  onChange={handleScheduleChange}
                />
              </Box>
              <Divider />

              {/* Area selection — net2 only */}
              {targetType === 'net2' && (
                <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2">Areas to apply:</Typography>
                    {loadingAreas && <CircularProgress size={16} />}
                    {!loadingAreas && availableAreas.length === 0 && (
                      <Button size="small" variant="text" onClick={() => handleTargetTypeChange('net2')}>
                        Fetch areas
                      </Button>
                    )}
                    {availableAreas.length > 0 && (
                      <Button size="small" variant="text" onClick={() =>
                        setSelectedAreaIds(
                          selectedAreaIds.length === availableAreas.length
                            ? []
                            : availableAreas.map(a => a.areaID ?? a.areaId)
                        )
                      }>
                        {selectedAreaIds.length === availableAreas.length ? 'Deselect all' : 'Select all'}
                      </Button>
                    )}
                  </Box>
                  {availableAreas.length > 0 && (
                    <FormGroup row>
                      {availableAreas.map((area) => {
                        const id = area.areaID ?? area.areaId;
                        return (
                          <Tooltip key={id} title={`Area ID: ${id}`}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={selectedAreaIds.includes(id)}
                                  onChange={() => handleAreaToggle(id)}
                                />
                              }
                              label={area.name || `Area ${id}`}
                            />
                          </Tooltip>
                        );
                      })}
                    </FormGroup>
                  )}
                </Box>
              )}

              {/* Device selection — suprema only */}
              {targetType === 'suprema' && (
                <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2">Gates (devices) to apply:</Typography>
                    {loadingGates && <CircularProgress size={16} />}
                    {!loadingGates && availableGates.length === 0 && (
                      <Button size="small" variant="text" onClick={() => handleTargetTypeChange('suprema')}>
                        Fetch gates
                      </Button>
                    )}
                    {availableGates.length > 0 && (
                      <Button size="small" variant="text" onClick={() =>
                        setSelectedGateKeys(
                          selectedGateKeys.length === availableGates.length
                            ? []
                            : availableGates.map(supremaGateKey)
                        )
                      }>
                        {selectedGateKeys.length === availableGates.length ? 'Deselect all' : 'Select all'}
                      </Button>
                    )}
                  </Box>
                  {availableGates.length > 0 && (
                    <FormGroup row>
                      {availableGates.map((gate) => {
                        const key = supremaGateKey(gate);
                        return (
                          <Tooltip key={key} title={`Device ${gate.deviceId} · gate ${gate.gateId}`}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={selectedGateKeys.includes(key)}
                                  onChange={() => handleSupremaGateToggle(key)}
                                />
                              }
                              label={gate.name || `Gate ${gate.gateId}`}
                            />
                          </Tooltip>
                        );
                      })}
                    </FormGroup>
                  )}
                </Box>
              )}

              <Divider />
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="subtitle2">Target Device:</Typography>
                  <ToggleButtonGroup
                    value={targetType}
                    exclusive
                    onChange={(e, val) => handleTargetTypeChange(val)}
                    size="small"
                  >
                    <ToggleButton value="net2">Net2</ToggleButton>
                    <ToggleButton value="suprema">Suprema</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button variant="outlined" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={handlePushToDevice}
                    disabled={pushingToDevice}
                    startIcon={pushingToDevice ? <CircularProgress size={20} /> : <Icon icon="mdi:cloud-upload" />}
                  >
                    Push to {targetType.toUpperCase()}
                  </Button>
                  <Button 
                    variant="outlined" 
                    onClick={handleSave}
                    startIcon={<Icon icon="mdi:content-save" />}
                  >
                    Save Local
                  </Button>
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Right Column - Preview & Saved */}
          <Grid xs={12} lg={4}>
            <AccessLevelPreview 
              contract={buildContract()}
              onCopy={() => {
                navigator.clipboard.writeText(JSON.stringify(buildContract(), null, 2));
                enqueueSnackbar('Contract copied to clipboard', { variant: 'success' });
              }}
            />

            {savedAccessLevels.length > 0 && (
              <Card sx={{ mt: 3 }}>
                <CardHeader 
                  title="Saved Access Levels"
                  subheader={`${savedAccessLevels.length} level(s) configured`}
                />
                <Box sx={{ p: 2 }}>
                  {savedAccessLevels.map((al) => {
                    const isSuprema = al.type?.startsWith('suprema');
                    const isNet2 = al.type?.startsWith('net2');
                    return (
                      <Card key={al.id} variant="outlined" sx={{ mb: 2, p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle1" fontWeight="medium">
                                {al.name}
                              </Typography>
                              {isSuprema && (
                                <Chip label={al.type} size="small" color="secondary" variant="outlined" />
                              )}
                              {isNet2 && (
                                <Chip label="net2" size="small" color="primary" variant="outlined" />
                              )}
                              {!isSuprema && !isNet2 && (
                                <Chip label={al.type || 'local'} size="small" variant="outlined" />
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {al.weeklySchedule?.days?.filter(d => d.enabled).length === 7
                                ? 'Always'
                                : al.weeklySchedule?.days?.filter(d => d.enabled).length === 0
                                  ? 'Never'
                                  : `${al.weeklySchedule?.days?.filter(d => d.enabled).length || 0} day(s) active`}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleLoadSaved(al)}
                              startIcon={<Icon icon="mdi:pencil-outline" width={16} />}
                            >
                              Load
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleDeleteSaved(al.id)}
                            >
                              <Icon icon="mdi:delete" width={20} />
                            </Button>
                          </Box>
                        </Box>
                      </Card>
                    );
                  })}
                </Box>
              </Card>
            )}
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

